import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { breadcrumbJsonLd } from '@/lib/structuredData';
import JsonLD from '@/components/JsonLD';

export default function LegalLease() {
  return (
    <>
      <Seo
        title={withTemplate('Lease Agreement - Legal Terms')}
        description="Terms and conditions for domain leasing with option to purchase."
        canonical={canonicalize('/legal/lease')}
      />

      <JsonLD
        data={breadcrumbJsonLd([
          { name: 'Home', url: canonicalize('/') },
          { name: 'Legal', url: canonicalize('/legal') },
          { name: 'Lease Agreement', url: canonicalize('/legal/lease') },
        ])}
      />

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-foreground">Lease Agreement</h1>
            <p className="text-lg text-muted-foreground">
              Domain License to Use + Option to Buy Agreement
            </p>
          </div>

          <Card className="apple-card">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">
                Domain Lease (License to Use) + Option to Buy (BIN) Agreement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-sm leading-relaxed">
              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  1. Agreement Overview
                </h3>
                <p className="text-muted-foreground">
                  This agreement grants you a monthly license to use the specified domain name while
                  maintaining an option to purchase the domain at the original Buy It Now (BIN)
                  price at any time during the lease period.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">2. License Grant</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Usage Rights:</strong> You receive exclusive right to use the domain for
                    your business purposes, including full DNS control, website hosting, email
                    setup, and all standard domain functions.
                  </p>
                  <p>
                    <strong>DNS Control:</strong> Complete DNS management access is provided
                    immediately upon lease activation.
                  </p>
                  <p>
                    <strong>Business Use:</strong> You may build websites, conduct business, and
                    generate revenue using the domain.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  3. Monthly Subscription
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Billing:</strong> Monthly subscription fees are billed in advance on
                    your chosen billing date.
                  </p>
                  <p>
                    <strong>Payment Methods:</strong> Credit card, ACH, or other approved payment
                    methods.
                  </p>
                  <p>
                    <strong>Late Payments:</strong> 5-day grace period. Service suspension after 10
                    days of non-payment.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  4. Option to Purchase (BIN)
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Purchase Right:</strong> You have the ongoing option to purchase the
                    domain at the original Buy It Now (BIN) price at any time during your active
                    lease.
                  </p>
                  <p>
                    <strong>Exercise Notice:</strong> Provide 24-hour written notice to exercise
                    your purchase option.
                  </p>
                  <p>
                    <strong>Price Protection:</strong> The BIN price remains fixed regardless of
                    market value changes.
                  </p>
                  <p>
                    <strong>Lease Payments:</strong> Previous lease payments do not count toward the
                    purchase price.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  5. Third-Party Purchase Protection
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Right of First Refusal:</strong> If we receive a purchase offer from a
                    third party at or above the BIN price, you have 48 hours to exercise your option
                    to purchase at BIN price.
                  </p>
                  <p>
                    <strong>Notification:</strong> We will notify you immediately of any third-party
                    purchase interest.
                  </p>
                  <p>
                    <strong>Priority:</strong> Your option to purchase takes priority over
                    third-party offers.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">6. Cancellation</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Your Right to Cancel:</strong> Cancel anytime with 30 days notice. No
                    cancellation penalties.
                  </p>
                  <p>
                    <strong>Service Until End of Period:</strong> Continue using the domain until
                    the end of your current billing period.
                  </p>
                  <p>
                    <strong>Data Backup:</strong> You have 30 days to backup your data after
                    cancellation.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  7. Ownership and Transfer
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Domain Ownership:</strong> We retain legal ownership of the domain
                    during the lease period.
                  </p>
                  <p>
                    <strong>Purchase Transfer:</strong> Upon exercise of purchase option and
                    payment, full ownership transfers to you.
                  </p>
                  <p>
                    <strong>Transfer Process:</strong> Domain transfer handled through established
                    registrar processes at no additional cost.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  8. Restrictions and Compliance
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Legal Use:</strong> Domain must be used for lawful purposes only.
                  </p>
                  <p>
                    <strong>Trademark Compliance:</strong> Respect third-party trademark rights.
                  </p>
                  <p>
                    <strong>Content Restrictions:</strong> No illegal content, spam, or activities
                    that violate our acceptable use policy.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  9. Support and Maintenance
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Domain Management:</strong> We handle domain registration maintenance,
                    renewals, and registrar compliance.
                  </p>
                  <p>
                    <strong>Technical Support:</strong> DNS configuration assistance and
                    domain-related technical support included.
                  </p>
                  <p>
                    <strong>Response Time:</strong> Support requests answered within 24 hours during
                    business days.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-foreground mb-3">10. Governing Law</h3>
                <p className="text-muted-foreground">
                  This agreement is governed by the laws of [State/Country] and any disputes will be
                  resolved through binding arbitration or in the appropriate courts.
                </p>
              </section>

              <section className="mt-8 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Questions?</strong> Contact us at legal@attaindomains.com or through our
                  support system. This agreement may be updated from time to time with 30 days
                  notice to active lessees.
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
